import {
    EProgrammingStatus,
    EProgrammingTravelType,
    Programming,
    statusOptions,
    UpdateProgrammingRequest,
} from "../../models/programming";
import { normalizeProgrammingStatus } from "../../shared/utils/torre-tracking-utils";
import { canEditProgrammingRow } from './programming-edit-access';

/** Colunas da grade expandida, na ordem operacional da planilha TNE (só campos de Programação). */
export const SPREADSHEET_COLUMN_KEYS = [
    'placa',
    'sr1',
    'sr2',
    'volume',
    'cliente',
    'tipo',
    'status',
    'fechamento',
    'motorista',
    'origem',
    'destino',
    'produto',
    'prevDescarga',
    'prevSaida',
    'acoes',
] as const;

export type SpreadsheetColumnKey = typeof SPREADSHEET_COLUMN_KEYS[number];

export const SPREADSHEET_COLUMN_LABELS: Record<SpreadsheetColumnKey, string> = {
    placa: 'Placa',
    sr1: 'SR1',
    sr2: 'SR2',
    volume: 'Volume m³',
    cliente: 'Cliente / operação',
    tipo: 'Carregado/Vazio',
    status: 'Status',
    fechamento: 'Fechamento',
    motorista: 'Motorista',
    origem: 'Origem',
    destino: 'Destino',
    produto: 'Produto',
    prevDescarga: 'Prev. descarga',
    prevSaida: 'Prev. saída',
    acoes: 'Ações',
};

export const SPREADSHEET_COLUMN_WIDTHS: string[] = [
    '120px',
    '110px',
    '110px',
    '90px',
    '160px',
    '120px',
    '160px',
    '200px',
    '180px',
    '160px',
    '160px',
    '140px',
    '160px',
    '160px',
    '64px',
];

export const SPREADSHEET_COLUMN_COUNT = SPREADSHEET_COLUMN_KEYS.length;
export const SPREADSHEET_PAGE_SIZE = 50;
export const SPREADSHEET_TABLE_MIN_WIDTH = 2060;

const TERMINAL_STATUSES = [
    EProgrammingStatus.Completed,
    EProgrammingStatus.Finished,
    EProgrammingStatus.Cancelled,
];

export function isSpreadsheetTerminalStatus(status: string | number): boolean {
    const normalized = normalizeProgrammingStatus(status);
    return normalized !== null && TERMINAL_STATUSES.includes(normalized);
}

/** Status operacionais da grade: cancelar/concluir exige o fluxo com justificativa / complete. */
export const SPREADSHEET_INLINE_STATUS_OPTIONS = statusOptions.filter(
    (option) => !isSpreadsheetTerminalStatus(option.value)
);

export function resolveSpreadsheetInlineEditAccess(opts: {
    profile?: string | null;
    clientId?: string | null;
}): { canInlineEdit: boolean; lockedClientId?: string } {
    if (opts.profile === 'Admin') {
        return { canInlineEdit: true };
    }
    if (opts.profile === 'Manager') {
        if (!opts.clientId) {
            return { canInlineEdit: false };
        }
        return { canInlineEdit: true, lockedClientId: opts.clientId };
    }
    return { canInlineEdit: false };
}

export function canEditSpreadsheetRow(opts: {
    canInlineEdit: boolean;
    lockedClientId?: string;
    clientId?: string;
    status: string | number;
    isManager?: boolean;
    profile?: string | null;
    userId?: string | null;
    userEmail?: string | null;
    createdByUserId?: string;
    createdBy?: string;
}): boolean {
    if (!opts.canInlineEdit) return false;
    if (opts.isManager && !opts.lockedClientId) return false;
    if (!opts.clientId) return false;

    return canEditProgrammingRow({
        profile: opts.profile,
        userId: opts.userId,
        userEmail: opts.userEmail,
        managerClientId: opts.lockedClientId,
        programming: {
            clientId: opts.clientId,
            status: opts.status,
            createdByUserId: opts.createdByUserId,
            createdBy: opts.createdBy,
        },
    });
}

function asTravelType(value: EProgrammingTravelType | string): EProgrammingTravelType | string {
    if (typeof value === 'number') return value;
    if (value === 'Empty') return EProgrammingTravelType.Empty;
    if (value === 'Loaded') return EProgrammingTravelType.Loaded;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
}

export function toUpdateProgrammingRequest(
    programming: Programming,
    patch: Partial<UpdateProgrammingRequest> = {}
): UpdateProgrammingRequest {
    const payload: UpdateProgrammingRequest = {
        clientId: patch.clientId ?? programming.clientId,
        mainVehiclePlateId: patch.mainVehiclePlateId ?? programming.mainVehiclePlateId,
        semiTrailer1Id: patch.semiTrailer1Id ?? programming.semiTrailer1Id,
        semiTrailer2Id: patch.semiTrailer2Id ?? programming.semiTrailer2Id,
        volume: patch.volume ?? programming.volume,
        productId: patch.productId ?? programming.productId,
        driverId: patch.driverId ?? programming.driverId,
        travelType: patch.travelType ?? asTravelType(programming.travelType),
        routeId: patch.routeId ?? programming.routeId,
        originReferenceId: patch.originReferenceId ?? programming.originReferenceId,
        destinationReferenceId: patch.destinationReferenceId ?? programming.destinationReferenceId,
        returnDestinationReferenceId: patch.returnDestinationReferenceId ?? programming.returnDestinationReferenceId,
        expectedDepartureTime: patch.expectedDepartureTime
            ?? programming.expectedOriginDepartureAt
            ?? programming.expectedDepartureTime,
        expectedArrivalTime: patch.expectedArrivalTime
            ?? programming.expectedDestinationArrivalAt
            ?? programming.expectedArrivalTime,
        expectedOriginDepartureAt: patch.expectedOriginDepartureAt
            ?? programming.expectedOriginDepartureAt
            ?? programming.expectedDepartureTime,
        expectedDestinationArrivalAt: patch.expectedDestinationArrivalAt
            ?? programming.expectedDestinationArrivalAt
            ?? programming.expectedArrivalTime,
        expectedDestinationDepartureAt: patch.expectedDestinationDepartureAt
            ?? programming.expectedDestinationDepartureAt,
        expectedReturnArrivalAt: patch.expectedReturnArrivalAt ?? programming.expectedReturnArrivalAt,
        distanceKm: patch.distanceKm ?? programming.distanceKm,
        isReturn: patch.isReturn ?? programming.isGoAndReturn ?? programming.isReturn,
        isGoAndReturn: patch.isGoAndReturn ?? programming.isGoAndReturn ?? programming.isReturn,
    };
    if (Object.prototype.hasOwnProperty.call(patch, 'status')) {
        const nextStatus = patch.status ?? normalizeProgrammingStatus(programming.status) ?? undefined;
        if (nextStatus !== undefined && !isSpreadsheetTerminalStatus(nextStatus)) {
            payload.status = nextStatus;
        }
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'auditSource')) {
        payload.auditSource = patch.auditSource;
    }
    return payload;
}
